import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import SubtleBadge from 'components/common/SubtleBadge';
import { getEntetesSession } from 'data/Finance/caisse';

const columns = [
  {
    accessorKey: 'nfact',
    header: 'N° facture',
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'date',
    header: 'Date',
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
    accessorFn: row => {
      return (
        row.date &&
        new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(row.date))
      );
    },
  },
  {
    accessorKey: 'client',
    header: 'Client',
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'opération',
    header: 'Opération',
    meta: {
      headerProps: { className: 'text-start text-900 w-2' },
      cellProps: { className: 'ps-2 text-start w-5 fs-10' },
    },
    cell: ({ row: { original } }) => {
      const { numfacture } = original;
      return (
        <SubtleBadge
          pill
          bg={numfacture === '0' ? 'primary' : 'danger'}
          className="d-flex flex-center"
        >
          <p className="mb-0">{numfacture === '0' ? 'Vente' : 'Retour'}</p>
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'mntdtfin',
    header: 'Montant vente',
    accessorFn: row => (row.mntdtfin ? parseFloat(row.mntdtfin).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'montant',
    header: 'Reglement',
    accessorFn: row => (row.montant ? parseFloat(row.montant).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900', style: { maxWidth: '100px' } },
      cellProps: {
        className: 'font-sans-serif white-space-nowrap fs-10',
        style: { maxWidth: '100px' },
      },
    },
  },
  {
    accessorKey: 'solde',
    header: 'Solde',
    accessorFn: row =>
      row.mntdtfin
        ? (parseFloat(row.mntdtfin || 0) - parseFloat(row.montant || 0)).toFixed(3)
        : '0.000',
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'espece',
    header: 'Espece',
    accessorFn: row => (row.espece ? parseFloat(row.espece).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'Cheque',
    header: 'Chèque',
    accessorFn: row => (row.Cheque ? parseFloat(row.Cheque).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'TPE',
    header: 'TPE',
    accessorFn: row => (row.TPE ? parseFloat(row.TPE).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'SODEXO',
    header: 'SODEXO',
    accessorFn: row => (row.SODEXO ? parseFloat(row.SODEXO).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'Engagement',
    header: 'Engagement',
    accessorFn: row => (row.Engagement ? parseFloat(row.Engagement).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'Traites',
    header: 'Traites',
    accessorFn: row => (row.Traites ? parseFloat(row.Traites).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'Autres',
    header: 'Autres',
    accessorFn: row => (row.Autres ? parseFloat(row.Autres).toFixed(3) : '0.000'),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'vendeur',
    header: 'Vendeur',
    meta: {
      headerProps: {
        className: 'text-center text-900',
      },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
];

const conversion = {
  espece: 'Espèce',
  Cheque: 'Chèque',
  TPE: 'TPE',
  SODEXO: 'Sodexo',
  Engagement: 'Engagement',
  Traites: 'Traite',
  Autres: 'Autres',
};

const EnteteRecettes = ({ session, magasin, showDetails, montants = [] }) => {
  const [entetes, setEntetes] = useState([]);
  const [loading, setLoading] = useState(false);
  const table = useAdvanceTable({
    data: entetes,
    columns: columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 5,
    selectionColumnWidth: 52,
    getRowId: row => row.ID,
  });

  useEffect(() => {
    const fetchEntetes = async () => {
      try {
        setLoading(true);
        const response = await getEntetesSession({ ID: session, magasin });
        setEntetes(response);
      } catch (error) {
        console.error('Error fetching entetes:', error);
      } finally {
        setLoading(false);
      }
    };
    if (showDetails) {
      fetchEntetes();
    } else {
      setEntetes([]);
    }
  }, [session, magasin, showDetails]);

  const Recapitulatif = entetes.reduce(
    (acc, entete) => {
      acc.espece += parseFloat(entete.espece || 0);
      acc.Cheque += parseFloat(entete.Cheque || 0);
      acc.TPE += parseFloat(entete.TPE || 0);
      acc.SODEXO += parseFloat(entete.SODEXO || 0);
      acc.Engagement += parseFloat(entete.Engagement || 0);
      acc.Traites += parseFloat(entete.Traites || 0);
      acc.Autres += parseFloat(entete.Autres || 0);
      return acc;
    },
    {
      espece: 0,
      Cheque: 0,
      TPE: 0,
      SODEXO: 0,
      Engagement: 0,
      Traites: 0,
      Autres: 0,
    }
  );
  const RecapitulatifSession = entetes.reduce(
    (acc, entete) => {
      acc.montant += parseFloat(entete.mntdtfin || 0);
      acc.reglement += parseFloat(entete.montant || 0);
      return acc;
    },
    {
      montant: 0,
      reglement: 0,
    }
  );

  return (
    <>
      <Card className="mt-3">
        <Card.Header>
          <h5>Recettes Boutiques</h5>
        </Card.Header>
        <Card.Body>
          <div>
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  bordered: true,
                  striped: true,
                  className: 'fs-10 mb-0 overflow-hidden',
                }}
              />
              <AdvanceTableFooter
                rowInfo
                navButtons
                perPage={5}
                rowsPerPageSelection={true}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200, 250]}
                viewAllBtn={true}
              />
            </AdvanceTableProvider>
          </div>
        </Card.Body>
      </Card>
      <Card className="mt-3">
        <Card.Header>
          <h5>Recapitulatif session</h5>
        </Card.Header>
        <Card.Body>
          {entetes && (
            <div className="d-flex gap-4 justify-content-between">
              <div>
                <div className="d-flex gap-4">
                  <div key={'entete'} className="mb-2 d-flex flex-column">
                    <span className="fw-bold">{'Type'}</span>
                    <span className="fw-bold">{'Montant session'}</span>
                    <span className="fw-bold">{'Montant reçu'}</span>
                  </div>
                  {Object.entries(Recapitulatif).map(([key, value]) => (
                    <div key={key} className="mb-2 d-flex flex-column">
                      <span className="fw-bold">{conversion[key]}</span>
                      {value.toLocaleString('fr-FR', {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      })}
                      <span>
                        {montants
                          .find(montant => montant.type === conversion[key])
                          ?.montantValide?.toLocaleString('fr-FR', {
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3,
                          }) || '0.000'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex gap-4">
                {Object.entries(RecapitulatifSession).map(([key, value]) => (
                  <div key={key} className="mb-2 d-flex flex-column">
                    <span
                      className="fw-bold"
                      style={{ backgroundColor: 'GrayText', color: 'white', padding: '2px' }}
                    >
                      {key}
                    </span>
                    {value.toLocaleString('fr-FR', {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3,
                    })}
                  </div>
                ))}
                <div key="solde" className="mb-2 d-flex flex-column">
                  <span
                    className="fw-bold"
                    style={{ backgroundColor: 'GrayText', color: 'white', padding: '2px' }}
                  >
                    {'Solde'}
                  </span>
                  {(RecapitulatifSession.montant - RecapitulatifSession.reglement).toLocaleString(
                    'fr-FR',
                    {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3,
                    }
                  )}
                </div>
                <div key="montantRecu" className="mb-2 d-flex flex-column">
                  <span
                    className="fw-bold"
                    style={{ backgroundColor: 'GrayText', color: 'white', padding: '2px' }}
                  >
                    {'Montant Reçu'}
                  </span>
                  {montants
                    .reduce((acc, montant) => acc + parseFloat(montant.montantValide || 0), 0)
                    .toLocaleString('fr-FR', {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3,
                    })}
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default EnteteRecettes;
